import React, { useEffect, useState, useRef, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../context/auth-context";
import { Loader2, AlertCircle, QrCode, XCircle, RefreshCw, User } from "lucide-react";
import axios from "axios";

const BASE_URL = process.env.REACT_APP_BASE_URL;

// ── Countdown hook ────────────────────────────────────────────────────────────
function useCountdown(active, seconds, onDone) {
  const [remaining, setRemaining] = useState(seconds);
  useEffect(() => {
    if (!active) { setRemaining(seconds); return; }
    setRemaining(seconds);
    const interval = setInterval(() => {
      setRemaining((r) => {
        if (r <= 1) { clearInterval(interval); onDone(); return 0; }
        return r - 1;
      });
    }, 1000);
    return () => clearInterval(interval);
  }, [active, seconds, onDone]);
  return remaining;
}

// ── Helpers ───────────────────────────────────────────────────────────────────
const formatDateTime = (isoString) => {
  if (!isoString) return { date: "", time: "" };
  const dateObj   = new Date(isoString);
  const day       = dateObj.getUTCDate().toString().padStart(2, "0");
  const monthNames = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
  const month     = monthNames[dateObj.getUTCMonth()];
  const year      = dateObj.getUTCFullYear();
  const date      = `${day} ${month} ${year}`;
  let hours       = dateObj.getUTCHours();
  const minutes   = dateObj.getUTCMinutes().toString().padStart(2, "0");
  const ampm      = hours >= 12 ? "PM" : "AM";
  hours = hours % 12 || 12;
  return { date, time: `${hours}:${minutes} ${ampm}` };
};

const parseQrUrl = (raw) => {
  try {
    const url        = new URL(raw);
    const p          = url.searchParams;
    const userId     = p.get("userId");
    const currentDay = p.get("currentDay");
    const status     = p.get("status");
    if (userId && currentDay && status) return { userId, currentDay, status };
  } catch (_) {}
  return null;
};

const supportsBarcodeDetector = () =>
  typeof window !== "undefined" && "BarcodeDetector" in window;

// ── MarkAttendance ────────────────────────────────────────────────────────────
const MarkAttendance = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const {
    user,
    loading: authLoading,
    token,
    cachedLocation,
    cacheLocation,
  } = useAuth();
  const faceOriginRef = useRef(Boolean(location.state?.faceScanParams));

  // Company resolved on the dashboard (SupervisorView) before the user could
  // even reach this page — arrives via router state.
  const passedCompany = location.state?.company || null;
  const passedCompanyId   = passedCompany?._id || passedCompany?.companyId || "";
  const passedCompanyName = passedCompany?.name || passedCompany?.companyName || "";

  // scanner refs
  const videoRef           = useRef(null);
  const streamRef          = useRef(null);
  const barcodeDetectorRef = useRef(null);
  const animationRef       = useRef(null);
  const lastScannedRef     = useRef("");
  const scanningActiveRef  = useRef(false);
  const lastProcessTimeRef = useRef(0);
  const [camError,           setCamError]           = useState("");
  const [usingNativeScanner, setUsingNativeScanner] = useState(supportsBarcodeDetector());

  const apiCallMade = useRef(false);
  const [featureAccess, setFeatureAccess] = useState([]);
  const [userRole,      setUserRole]      = useState("");

  const [state, setState] = useState({
    status: "idle",
    error: null,
    attendanceData: null,
    userProfile: null,
    showConfirmation: false,
    rejected: false,
    userLocation: null,
    locationError: null,
    isGettingLocation: false,
    locationValidated: false,
    busStatus: false,
    haveFood: false,
    imageLoading: true,
    imageError: false,
    departments: [],
    selectedDepartment: "",
    loadingDepartments: false,
    departmentsError: null,
    params: null,
  });

  const isScannerFlow = !new URLSearchParams(location.search).get("userId");

  // ── resetToScanner ────────────────────────────────────────────────────────
  const resetToScanner = useCallback(() => {
    if (faceOriginRef.current) {
      navigate("/lkart/admin/face-scan", { replace: true, state: {} });
      return;
    }
    lastScannedRef.current = "";
    apiCallMade.current    = false;
    setState({
      status: "scanning",
      error: null,
      attendanceData: null,
      userProfile: null,
      showConfirmation: false,
      rejected: false,
      userLocation: null,
      locationError: null,
      isGettingLocation: false,
      locationValidated: false,
      busStatus: false,
      haveFood: false,
      imageLoading: true,
      imageError: false,
      departments: [],
      selectedDepartment: "",
      loadingDepartments: false,
      departmentsError: null,
      params: null,
    });
  }, [navigate]);

  const countdown = useCountdown(
    (state.status === "success" || state.status === "rejected") && isScannerFlow,
    5,
    resetToScanner
  );

  // ── determine mode on mount ───────────────────────────────────────────────
  useEffect(() => {
    if (authLoading) return;
    if (!user) {
      const returnUrl = encodeURIComponent(location.pathname + location.search);
      navigate(`/lkart/admin?returnUrl=${returnUrl}`);
      return;
    }
    if (!passedCompany) {
      setState((prev) => ({
        ...prev,
        status: "error",
        error: "No company found. Please open Scan QR from the dashboard.",
      }));
      return;
    }
    const sp = new URLSearchParams(location.search);
    const hasUrlParams = sp.get("userId") && sp.get("currentDay") && sp.get("status");
    const hasFaceParams = Boolean(location.state?.faceScanParams);
    if (!hasUrlParams && !hasFaceParams) {
      setState((prev) => ({ ...prev, status: "scanning" }));
    }
  }, [authLoading, user]); // eslint-disable-line

  // ── camera ────────────────────────────────────────────────────────────────
  const startCamera = useCallback(async () => {
    setCamError("");
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: "environment", width: { ideal: 720 }, height: { ideal: 1280 } },
        audio: false,
      });
      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        await videoRef.current.play();
      }
    } catch {
      setCamError("Camera access denied. Please allow camera permissions and refresh.");
    }
  }, []);

  const stopCamera = useCallback(() => {
    if (animationRef.current) {
      if (typeof animationRef.current === "number") cancelAnimationFrame(animationRef.current);
      else clearTimeout(animationRef.current);
    }
    scanningActiveRef.current = false;
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
    barcodeDetectorRef.current = null;
  }, []);

  const handleQrDetected = useCallback((params) => {
    stopCamera();
    apiCallMade.current = false;
    setState((prev) => ({ ...prev, status: "loading", params, error: null }));
  }, [stopCamera]);

  const startNativeScanning = useCallback(async () => {
    if (!videoRef.current || !scanningActiveRef.current) return;
    if (!window.BarcodeDetector) { setUsingNativeScanner(false); return; }
    try {
      if (!barcodeDetectorRef.current)
        barcodeDetectorRef.current = new window.BarcodeDetector({ formats: ["qr_code"] });
      const detect = async () => {
        if (!scanningActiveRef.current || !videoRef.current || videoRef.current.readyState !== 4) {
          animationRef.current = requestAnimationFrame(detect); return;
        }
        try {
          const barcodes = await barcodeDetectorRef.current.detect(videoRef.current);
          if (barcodes.length > 0 && barcodes[0].rawValue) {
            const raw = barcodes[0].rawValue;
            if (raw !== lastScannedRef.current) {
              const parsed = parseQrUrl(raw);
              if (parsed) { lastScannedRef.current = raw; scanningActiveRef.current = false; handleQrDetected(parsed); return; }
            }
          }
        } catch (err) { console.debug("Detection error:", err); }
        if (scanningActiveRef.current) {
          const now = performance.now();
          if (now - lastProcessTimeRef.current >= 33) { lastProcessTimeRef.current = now; animationRef.current = requestAnimationFrame(detect); }
          else animationRef.current = setTimeout(() => requestAnimationFrame(detect), 10);
        }
      };
      detect();
    } catch (err) { console.error("Native scanner error:", err); setUsingNativeScanner(false); }
  }, [handleQrDetected]);

  const startFallbackScanning = useCallback(() => {
    const canvas = document.createElement("canvas");
    const ctx    = canvas.getContext("2d", { willReadFrequently: true });
    let frameCount = 0;
    const PROCESS_EVERY_N_FRAMES = 3, DECODE_WIDTH = 360;
    const scan = async () => {
      if (!scanningActiveRef.current || !videoRef.current || videoRef.current.readyState !== 4) {
        animationRef.current = requestAnimationFrame(scan); return;
      }
      frameCount++;
      if (frameCount % PROCESS_EVERY_N_FRAMES !== 0) { animationRef.current = requestAnimationFrame(scan); return; }
      const now = performance.now();
      if (now - lastProcessTimeRef.current < 100) { animationRef.current = requestAnimationFrame(scan); return; }
      lastProcessTimeRef.current = now;
      try {
        const video = videoRef.current;
        const scale = DECODE_WIDTH / video.videoWidth;
        const w = DECODE_WIDTH, h = Math.round(video.videoHeight * scale);
        if (canvas.width !== w || canvas.height !== h) { canvas.width = w; canvas.height = h; }
        ctx.drawImage(video, 0, 0, w, h);
        const imageData = ctx.getImageData(0, 0, w, h);
        const jsQR = (await import("jsqr")).default;
        const code = jsQR(imageData.data, imageData.width, imageData.height, { inversionAttempts: "dontInvert" });
        if (code && code.data && code.data !== lastScannedRef.current) {
          const parsed = parseQrUrl(code.data);
          if (parsed) { lastScannedRef.current = code.data; scanningActiveRef.current = false; handleQrDetected(parsed); return; }
        }
      } catch (err) { console.debug("QR scan error:", err); }
      if (scanningActiveRef.current) animationRef.current = requestAnimationFrame(scan);
    };
    scan();
  }, [handleQrDetected]);

  useEffect(() => {
    if (state.status === "scanning") { startCamera(); return () => stopCamera(); }
  }, [state.status, startCamera, stopCamera]);

  useEffect(() => {
    if (state.status !== "scanning" || !videoRef.current) return;
    scanningActiveRef.current = true;
    if (usingNativeScanner && supportsBarcodeDetector()) startNativeScanning();
    else startFallbackScanning();
    return () => {
      scanningActiveRef.current = false;
      if (animationRef.current) {
        if (typeof animationRef.current === "number") cancelAnimationFrame(animationRef.current);
        else clearTimeout(animationRef.current);
      }
    };
  }, [state.status, usingNativeScanner, startNativeScanning, startFallbackScanning]);

  // ── API helpers ───────────────────────────────────────────────────────────
  const fetchUserProfile = async (userId) => {
    try {
      const response = await axios.get(`${BASE_URL}/lenskart/profile/${userId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || error.response?.data?.error || "Failed to fetch user profile");
    }
  };

  const hasFeatureAccess = (featureName) => {
    if (userRole === "Supervisor") return true;
    if (userRole === "Admin") return featureAccess.includes(featureName);
    return false;
  };

  useEffect(() => {
    if (userRole && userRole !== "Supervisor" && !hasFeatureAccess("Mark Attendance")) {
      setState((prev) => ({ ...prev, status: "error", error: "You do not have permission to mark attendance.", attendanceData: null }));
    }
  }, [userRole, featureAccess]); // eslint-disable-line

  const fetchDepartmentsBySkillsAndCompany = async (skills, skillIds, companyId) => {
    if (!companyId) { setState((prev) => ({ ...prev, departments: [], selectedDepartment: "" })); return; }
    try {
      setState((prev) => ({ ...prev, loadingDepartments: true, departmentsError: null }));
      const skillsArray = skills.split(",").map((s) => s.trim()).filter(Boolean);
      const response = await axios.post(
        `${BASE_URL}/lenskart/getDepartments/Skills`,
        { skills: skillsArray, skillIds, companyId },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (response.data && Array.isArray(response.data.departments)) {
        const formattedDepartments = response.data.departments.map((dept) => ({ _id: dept._id, name: dept.department }));
        setState((prev) => ({ ...prev, departments: formattedDepartments, selectedDepartment: "" }));
      } else {
        throw new Error(response.data?.msg || response.data?.error || "Invalid departments data received");
      }
    } catch (error) {
      setState((prev) => ({ ...prev, departmentsError: error.response?.data?.message || error.message || "Failed to load departments list", departments: [] }));
    } finally {
      setState((prev) => ({ ...prev, loadingDepartments: false }));
    }
  };

  const markAttendance = async (userId, currentDay, status, busStatus, haveFood, companyId, departmentName, departmentId , mode) => {
    try {
      const payload = { userId, date: currentDay, status, companyId, department: departmentName, departmentId };
   if (status === "entry") {
      payload.busEntry = Boolean(busStatus);
      payload.ModeOfEntry = mode;
    } else if (status === "exit") {
      payload.busExit = Boolean(busStatus);
      payload.haveFood = Boolean(haveFood);
      payload.ModeOfExit = mode;
    }
      const response = await axios.post(`${BASE_URL}/lenskart-admin/mark-attendance`, payload, { headers: { Authorization: `Bearer ${token}` } });
      if (response.data.success === false) throw new Error(response.data.message || "Operation failed");
      return response.data;
    } catch (error) {
      if (!error.response && !error.request) throw error;
      if (error.response) throw new Error(error.response.data?.message || "Server error occurred");
      throw new Error("Network error. Please check your connection.");
    }
  };

  // ── main data-fetch effect ────────────────────────────────────────────────
  useEffect(() => {
    const processInitialData = async () => {
      if (apiCallMade.current) return;
      if (authLoading) return;
      if (!user) return;
      if (!passedCompany) return;

      let userId, currentDay, status;
      const sp = new URLSearchParams(location.search);
      const urlUserId = sp.get("userId"), urlCurrentDay = sp.get("currentDay"), urlStatus = sp.get("status");
      const faceParams = location.state?.faceScanParams;

      if (urlUserId && urlCurrentDay && urlStatus) {
        userId = urlUserId; currentDay = urlCurrentDay; status = urlStatus;
      } else if (faceParams) {
        userId = faceParams.userId; currentDay = faceParams.currentDay; status = faceParams.status;
      } else if (state.params) {
        userId = state.params.userId; currentDay = state.params.currentDay; status = state.params.status;
      } else {
        return;
      }

      if (state.status !== "loading" && state.status !== "idle") return;

      try {
        apiCallMade.current = true;
        setState((prev) => ({ ...prev, status: "loading" }));

        const profileData = await fetchUserProfile(userId);

        try {
          const verifyResponse = await axios.get(`${BASE_URL}/lenskart/verify-token`, { headers: { Authorization: `Bearer ${token}` } });
          if (verifyResponse.data?.success && verifyResponse.data?.user) {
            const userData = verifyResponse.data.user;
            setFeatureAccess(userData.featureAccess || []);
            setUserRole(userData.role || "");
          }
        } catch (error) { console.error("Failed to fetch user permissions:", error); }

        if (!profileData.location) throw new Error("User location not found in profile");
        if (!profileData.skills) throw new Error("Skills missing in user profile");

        setState((prev) => ({
          ...prev, status: "pending", error: null, attendanceData: null,
          userProfile: profileData, showConfirmation: true,
          params: { userId, currentDay, status }, imageLoading: true, imageError: false,
        }));
      } catch (error) {
        setState((prev) => ({ ...prev, status: "error", error: error.message || "Failed to fetch user data. Please try again.", attendanceData: null }));
      }
    };
    processInitialData();
  }, [authLoading, location, user, token, state.params, state.status]); // eslint-disable-line

  // ── departments load off the passed company ─────────────────────────────
  useEffect(() => {
    if (passedCompanyId && state.userProfile?.skills) {
      fetchDepartmentsBySkillsAndCompany(state.userProfile.skills, state.userProfile.skillIds, passedCompanyId);
    }
  }, [passedCompanyId, state.userProfile?.skills]); // eslint-disable-line

  const getLocation = () =>
    new Promise((resolve, reject) => {
      setState((prev) => ({ ...prev, isGettingLocation: true, locationError: null }));
      if (!navigator.geolocation) { reject(new Error("Your browser does not support location tracking")); return; }
      navigator.geolocation.getCurrentPosition(
        (position) => resolve({ latitude: position.coords.latitude, longitude: position.coords.longitude, accuracy: position.coords.accuracy }),
        (error) => {
          let errorMessage;
          switch (error.code) {
            case error.PERMISSION_DENIED:
              errorMessage =
                `Please enable location permissions to mark attendance.\n\n` +
                `To enable location permissions for this site:\n` +
                `1. Tap the three-dot menu (⋮) in the top-right corner of Chrome.\n` +
                `2. From the dropdown menu, select "Settings".\n` +
                `3. Scroll down and tap "Site settings".\n` +
                `4. Under the "Permissions" section, tap "Location".\n` +
                `5. In the "Blocked" section, find and tap on the site: https://jobs.factrika.com\n` +
                `6. Select "Allow" to grant location permissions.\n` +
                `7. Go back to the site and refresh the page to try again.\n`;
              break;
            case error.POSITION_UNAVAILABLE: errorMessage = "Location information is unavailable"; break;
            case error.TIMEOUT:              errorMessage = "Location request timed out"; break;
            default:                         errorMessage = "Failed to get location";
          }
          reject(new Error(errorMessage));
        },
        { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 }
      );
    });

  // ── Validate a location against the server ────────────────────────────
  const validateLocationWithServer = async (loc) => {
    const queryParams = new URLSearchParams({
      latitude: loc.latitude,
      longitude: loc.longitude,
    });
    const response = await fetch(
      `${BASE_URL}/lenskart/location?${queryParams.toString()}`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      }
    );
    if (!response.ok) throw new Error("Failed to validate location with server");
    const data = await response.json();
    if (!data.inRange) {
      throw new Error(data.message || data.error || "Scanning is allowed within 50m range");
    }
    return data;
  };

  // ── Cache-first location process ──────────────────────────────────────
  const handleLocationProcess = async () => {
    try {
      setState((prev) => ({ ...prev, isGettingLocation: true, locationError: null }));

      // 1. If SupervisorView already cached a location for this session,
      //    reuse it and skip the browser GPS prompt entirely.
      if (cachedLocation?.latitude && cachedLocation?.longitude) {
        await validateLocationWithServer(cachedLocation);
        setState((prev) => ({
          ...prev,
          userLocation: cachedLocation,
          locationValidated: true,
          isGettingLocation: false,
        }));
        return true;
      }

      // 2. No cached location (hard refresh / deep-link) — fetch fresh,
      //    validate, then cache it for the rest of the session.
      const locationData = await getLocation();
      await validateLocationWithServer(locationData);
      cacheLocation(locationData);
      setState((prev) => ({
        ...prev,
        userLocation: locationData,
        locationValidated: true,
        isGettingLocation: false,
      }));
      return true;
    } catch (error) {
      setState((prev) => ({ ...prev, locationError: error.message, isGettingLocation: false }));
      return false;
    }
  };

  const handleDepartmentChange = (e) => setState((prev) => ({ ...prev, selectedDepartment: e.target.value || null }));
  const handleBusStatusChange  = ()  => setState((prev) => ({ ...prev, busStatus:  !prev.busStatus  }));
  const handleFoodStatusChange = ()  => setState((prev) => ({ ...prev, haveFood:   !prev.haveFood   }));
  const handleImageLoad        = ()  => setState((prev) => ({ ...prev, imageLoading: false, imageError: false }));
  const handleImageError       = ()  => setState((prev) => ({ ...prev, imageLoading: false, imageError: true  }));

  const handleAccept = async () => {
    try {
      if (!passedCompanyId) {
        setState((prev) => ({ ...prev, status: "error", error: "No company found. Please open Scan QR from the dashboard." }));
        return;
      }
      let locationValidated = state.locationValidated;
      if (!locationValidated) { locationValidated = await handleLocationProcess(); if (!locationValidated) return; }
      setState((prev) => ({ ...prev, status: "loading" }));
      const selectedDepartment = state.departments.find((dept) => dept._id === state.selectedDepartment);
      const departmentName = selectedDepartment?.name ?? "";
      const departmentId   = selectedDepartment?._id  ?? "";
      const response = await markAttendance(
        state.params.userId, state.params.currentDay, state.params.status,
        state.busStatus, state.haveFood, passedCompanyId, departmentName, departmentId,
        faceOriginRef.current ? "Face Scan" : "QR Scan"
      );
      if (response.success === true) {
        const { date, time } = formatDateTime(
          state.params.status === "entry" ? response.data.attendanceRecord.EntryDate : response.data.attendanceRecord.ExitDate
        );
        setState((prev) => ({
          ...prev, status: "success",
          attendanceData: { name: response.data.name, date, time, location: state.userLocation, status: state.params.status },
        }));
      }
    } catch (error) {
      setState((prev) => ({ ...prev, status: "error", error: error.message }));
    }
  };

  const handleReject = () => setState((prev) => ({ ...prev, status: "rejected", rejected: true }));

  // ── renders ───────────────────────────────────────────────────────────────

  // Spinner
  if (state.status === "loading" || state.isGettingLocation || state.loadingDepartments) {
    return (
      <div className="fixed inset-0 flex items-center justify-center bg-white z-50">
        <div className="flex flex-col items-center space-y-4">
          <Loader2 className="h-12 w-12 animate-spin text-indigo-600" />
          <div className="text-lg font-medium text-gray-700">
            {state.isGettingLocation  ? "Getting location..."    :
             state.loadingDepartments ? "Loading departments..." : "Processing..."}
          </div>
        </div>
      </div>
    );
  }

  // ── SCANNING ──────────────────────────────────────────────────────────────
  if (state.status === "scanning") {
    return (
      <div className="min-h-screen bg-gray-300 flex items-center justify-center">
        <div className="relative bg-gray-100 flex flex-col overflow-hidden" style={{ width: "min(768px, 100vw)", minHeight: "100vh" }}>
          <div className="flex items-center px-5 py-3 bg-white border-b border-gray-200 flex-shrink-0">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 rounded-full bg-indigo-100 flex items-center justify-center text-indigo-600 text-xs font-medium">AS</div>
              <span className="text-gray-900 font-medium text-sm">Admin Scanner</span>
              {user?.name && <span className="text-gray-400 text-xs">— {user.name}</span>}
              {passedCompanyName && <span className="text-indigo-600 text-xs font-medium">— {passedCompanyName}</span>}
              <span className={`text-xs px-2 py-0.5 rounded-full ${usingNativeScanner && supportsBarcodeDetector() ? "bg-green-100 text-green-600" : "bg-yellow-100 text-yellow-600"}`}>
                {usingNativeScanner && supportsBarcodeDetector() ? "⚡ Fast Scan" : "📱 Compatible Mode"}
              </span>
            </div>
          </div>
          <div className="flex-1 flex justify-center items-center p-4">
            <div className="w-full max-w-lg mx-auto">
              <div className="bg-gradient-to-br from-white via-[#f9f9ff] to-[#eef1ff] rounded-2xl shadow-lg border border-gray-100 p-4 flex flex-col items-center gap-4">
                <div className="flex items-center gap-2 self-start">
                  <QrCode className="text-indigo-600" size={20} />
                  <span className="text-base font-medium text-gray-900">Scan worker QR code</span>
                </div>
                {camError ? (
                  <div className="w-full bg-red-50 border border-red-200 rounded-xl p-6 text-center">
                    <XCircle className="text-red-400 mx-auto mb-2" size={32} />
                    <p className="text-red-600 text-sm mb-3">{camError}</p>
                    <button onClick={startCamera} className="inline-flex items-center gap-2 px-5 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg text-sm transition-colors">
                      <RefreshCw size={14} /> Retry camera
                    </button>
                  </div>
                ) : (
                  <>
                    <div className="relative rounded-xl overflow-hidden bg-[#0f0f14] border border-gray-200 w-full" style={{ aspectRatio: "3 / 4", maxWidth: "100%" }}>
                      <video ref={videoRef} className="w-full h-full object-cover" playsInline muted autoPlay />
                      {["top-3 left-3 border-t-2 border-l-2 rounded-tl","top-3 right-3 border-t-2 border-r-2 rounded-tr","bottom-3 left-3 border-b-2 border-l-2 rounded-bl","bottom-3 right-3 border-b-2 border-r-2 rounded-br"].map((cls, i) => (
                        <span key={i} className={`absolute w-7 h-7 border-indigo-400 ${cls}`} />
                      ))}
                      <div className="absolute left-3 right-3 h-0.5 bg-indigo-400/80 animate-[scanline_2s_linear_infinite]" />
                      <div className="absolute inset-0 pointer-events-none" style={{ background: "radial-gradient(ellipse 60% 60% at 50% 50%, transparent 0%, rgba(0,0,0,.45) 100%)" }} />
                    </div>
                    <p className="text-sm text-gray-500 text-center">
                      Point the camera at the worker's QR code
                      {!usingNativeScanner && " (Compatible mode — may be slower)"}
                    </p>
                    <button
                      onClick={() => navigate("/lkart/admin/face-scan", { replace: true, state: {} })}
                      className="flex items-center gap-2 text-xs text-indigo-500 hover:text-indigo-700 underline transition-colors mt-1"
                    >
                      <User size={13}/> Switch to Face Scan instead
                    </button>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
        <style>{`
          @keyframes scanline {
            0%   { top: 10px; opacity: 1; }
            50%  { opacity: .7; }
            100% { top: calc(100% - 10px); opacity: 1; }
          }
        `}</style>
      </div>
    );
  }

  // ── ERROR ─────────────────────────────────────────────────────────────────
  if (state.status === "error" || state.locationError || state.departmentsError) {
    return (
      <div className="fixed inset-0 flex items-center justify-center bg-white p-4">
        <div className="max-w-md w-full mx-4 p-6 bg-red-50 rounded-lg shadow-sm">
          <div className="text-red-600 text-center">
            <AlertCircle className="h-12 w-12 mx-auto mb-4" />
            <h3 className="text-lg font-medium mb-2">Error</h3>
            <p style={{ whiteSpace: "pre-line", textAlign: state.error ? "center" : "left", margin: 0 }}>
              {state.error || state.locationError || state.departmentsError}
            </p>
            {isScannerFlow && passedCompany && (
              <button onClick={resetToScanner} className="mt-4 px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 transition-colors">
                Scan Again
              </button>
            )}
            {!passedCompany && (
              <button onClick={() => navigate("/lkart/admin/view")} className="mt-4 px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 transition-colors">
                Back to Dashboard
              </button>
            )}
          </div>
        </div>
      </div>
    );
  }

  // ── REJECTED ──────────────────────────────────────────────────────────────
  if (state.status === "rejected") {
    return (
      <div className="fixed inset-0 flex items-center justify-center bg-white p-4">
        <div className="max-w-md w-full bg-red-50 rounded-lg shadow-lg p-6">
          <div className="text-red-600 text-center text-xl">Attendance is not marked.</div>

          {isScannerFlow && (
            <div className="mt-5">
              <div className="w-full bg-gray-100 rounded-full h-2 overflow-hidden mb-2">
                <div
                  className="h-2 bg-indigo-500 rounded-full transition-all duration-1000 ease-linear"
                  style={{ width: `${(countdown / 5) * 100}%` }}
                />
              </div>
              <p className="text-center text-sm text-gray-500 mb-4">
                Returning to scanner in{" "}
                <span className="font-semibold text-indigo-600">{countdown}</span>{" "}
                second{countdown !== 1 ? "s" : ""}…
              </p>

              <button
                onClick={resetToScanner}
                className="w-full py-3 bg-indigo-600 hover:bg-indigo-700 active:scale-95 text-white font-semibold rounded-xl transition-all text-sm"
              >
                Scan Next Worker
              </button>

              <button
                onClick={() =>
                  navigate(
                    faceOriginRef.current ? "/lkart/admin/attendance" : "/lkart/admin/face-scan",
                    { replace: true, state: {} }
                  )
                }
                className="w-full mt-2 text-xs text-indigo-500 hover:text-indigo-700 underline text-center"
              >
                Switch to {faceOriginRef.current ? "QR Code" : "Face Scan"} instead
              </button>
            </div>
          )}
        </div>
      </div>
    );
  }

  // ── CONFIRM ───────────────────────────────────────────────────────────────
  if (state.status === "pending" && state.showConfirmation) {
    const isEntry = state.params?.status === "entry";

    return (
      <div className="fixed inset-0 flex items-start justify-center bg-gray-100 p-3 sm:p-5 overflow-y-auto pt-16 sm:pt-20">
        <div className="w-full max-w-sm sm:max-w-md bg-white shadow-lg rounded-2xl overflow-hidden">

          <div className="px-4 pt-4 pb-0 text-center">
            <h2 className="text-base sm:text-lg font-semibold text-gray-900 tracking-wide">
              Mark {isEntry ? "Entry" : "Exit"}
            </h2>
          </div>

          <div className="px-4 py-3 sm:px-5 sm:py-4 flex flex-col gap-3">
            <div className="flex justify-center">
              {state.userProfile?.profile_photo ? (
                <div className="relative flex justify-center items-center w-full">
                  {state.imageLoading && (
                    <div className="absolute inset-0 flex items-center justify-center min-h-[96px]">
                      <Loader2 className="h-6 w-6 animate-spin text-indigo-600" />
                    </div>
                  )}
                  <img
                    src={
                      state.userProfile.profile_photo.startsWith("data:image") ||
                      state.userProfile.profile_photo.startsWith("https://")
                        ? state.userProfile.profile_photo
                        : `data:image/jpeg;base64,${state.userProfile.profile_photo}`
                    }
                    alt="Profile"
                    className={`rounded-xl object-contain max-h-52 sm:max-h-64 w-auto ${state.imageLoading ? "opacity-0" : "opacity-100"} transition-opacity duration-300`}
                    onLoad={handleImageLoad}
                    onError={handleImageError}
                    style={{ display: state.imageError ? "none" : "block" }}
                  />
                  {state.imageError && !state.imageLoading && (
                    <div className="w-16 h-16 rounded-full bg-gray-200 flex items-center justify-center">
                      <span className="text-xl text-gray-500">{state.userProfile?.name?.charAt(0)?.toUpperCase()}</span>
                    </div>
                  )}
                </div>
              ) : (
                <div className="w-16 h-16 rounded-full bg-gray-200 flex items-center justify-center">
                  <span className="text-xl text-gray-500">{state.userProfile?.name?.charAt(0)?.toUpperCase()}</span>
                </div>
              )}
            </div>

            <div className="text-center leading-tight">
              <p className="text-sm sm:text-base font-bold text-gray-900">{state.userProfile?.name}</p>
              <p className="text-sm sm:text-base font-bold text-gray-900">{state.userProfile?.workerCode}</p>
              <p className="text-xs sm:text-sm text-gray-500 mt-0.5">
                {state.userProfile?.skills
                  ? (() => {
                      const skills = state.userProfile.skills
                        .split(",")
                        .map(skill => skill.trim());

                      return skills.slice(0, 5).join(", ");
                    })()
                  : "N/A"}
              </p>
            </div>

            <div className="border-t border-gray-100" />

            <div className="flex flex-col gap-1">
              <label className="text-[10px] sm:text-xs font-semibold text-gray-400 uppercase tracking-wider">Company</label>
              <div className="px-3 py-2 bg-gray-50 rounded-lg text-sm font-medium text-gray-800 border border-gray-200">
                {passedCompanyName || "—"}
              </div>
            </div>

            <div className="flex flex-col gap-2">
              <label
                className="flex items-center gap-3 px-3 py-2.5 bg-gray-50 border border-gray-200 rounded-lg cursor-pointer select-none"
                onClick={handleBusStatusChange}
              >
                <input
                  type="checkbox"
                  checked={state.busStatus}
                  onChange={handleBusStatusChange}
                  className="h-4 w-4 text-green-600 rounded accent-green-600 flex-shrink-0"
                  onClick={(e) => e.stopPropagation()}
                />
                <span className="text-xs sm:text-sm text-gray-700">Taken Factorykaam Bus Service</span>
              </label>

              {!isEntry && (
                <label
                  className="flex items-center gap-3 px-3 py-2.5 bg-gray-50 border border-gray-200 rounded-lg cursor-pointer select-none"
                  onClick={handleFoodStatusChange}
                >
                  <input
                    type="checkbox"
                    checked={state.haveFood}
                    onChange={handleFoodStatusChange}
                    className="h-4 w-4 text-green-600 rounded accent-green-600 flex-shrink-0"
                    onClick={(e) => e.stopPropagation()}
                  />
                  <span className="text-xs sm:text-sm text-gray-700">Availed Food in Factory</span>
                </label>
              )}
            </div>

            {hasFeatureAccess("Mark Attendance") ? (
              <div className="flex gap-3 pt-1">
                <button
                  onClick={handleAccept}
                  className="flex-1 py-2.5 sm:py-3 text-sm sm:text-base font-semibold bg-green-600 text-white rounded-xl hover:bg-green-700 active:scale-95 transition-all"
                >
                  Accept
                </button>
                <button
                  onClick={handleReject}
                  className="flex-1 py-2.5 sm:py-3 text-sm sm:text-base font-semibold bg-red-500 text-white rounded-xl hover:bg-red-600 active:scale-95 transition-all"
                >
                  Reject
                </button>
              </div>
            ) : (
              <div className="py-2.5 px-4 bg-red-50 rounded-xl text-center">
                <p className="text-red-600 text-xs sm:text-sm font-semibold">You do not have permission to mark attendance</p>
              </div>
            )}

          </div>
        </div>
      </div>
    );
  }

  // ── SUCCESS ───────────────────────────────────────────────────────────────
  if (state.status === "success" && state.attendanceData) {
    return (
      <div className="fixed inset-0 flex items-center justify-center bg-white p-4">
        <div className="bg-white rounded-2xl shadow-lg w-full max-w-md overflow-hidden">

          <div className="bg-green-100 p-4">
            <h2 className="text-center text-green-700 text-lg sm:text-xl font-semibold">
              {state.attendanceData.status === "entry" ? "Entry Marked Successfully" : "Exit Marked Successfully"}
            </h2>
          </div>

          <div className="p-4">
            <table className="w-full border-collapse text-sm">
              <thead>
                <tr>
                  <th className="border border-gray-200 bg-gray-50 px-3 py-2 text-left font-semibold text-gray-600">Name</th>
                  <th className="border border-gray-200 bg-gray-50 px-3 py-2 text-left font-semibold text-gray-600">Date</th>
                  <th className="border border-gray-200 bg-gray-50 px-3 py-2 text-left font-semibold text-gray-600">Time</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td className="border border-gray-200 px-3 py-2">{state.attendanceData.name}</td>
                  <td className="border border-gray-200 px-3 py-2">{state.attendanceData.date}</td>
                  <td className="border border-gray-200 px-3 py-2">{state.attendanceData.time}</td>
                </tr>
              </tbody>
            </table>

            {isScannerFlow && (
              <div className="mt-5">
                <div className="w-full bg-gray-100 rounded-full h-2 overflow-hidden mb-2">
                  <div
                    className="h-2 bg-indigo-500 rounded-full transition-all duration-1000 ease-linear"
                    style={{ width: `${(countdown / 5) * 100}%` }}
                  />
                </div>
                <p className="text-center text-sm text-gray-500 mb-4">
                  Returning to scanner in{" "}
                  <span className="font-semibold text-indigo-600">{countdown}</span>{" "}
                  second{countdown !== 1 ? "s" : ""}…
                </p>

                <button
                  onClick={resetToScanner}
                  className="w-full py-3 bg-indigo-600 hover:bg-indigo-700 active:scale-95 text-white font-semibold rounded-xl transition-all text-sm"
                >
                  Scan Next Worker
                </button>

                <button
                  onClick={() =>
                    navigate(
                      faceOriginRef.current ? "/lkart/admin/attendance" : "/lkart/admin/face-scan",
                      { replace: true, state: {} }
                    )
                  }
                  className="w-full mt-2 text-xs text-indigo-500 hover:text-indigo-700 underline text-center"
                >
                  Switch to {faceOriginRef.current ? "QR Code" : "Face Scan"} instead
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  return null;
};

export default MarkAttendance;